<template>
    <div class="widget-inner">
        <div class="widget-empty-message" v-if="!entries.length">
            <div :style="{color}">
                {{widget.emptyMessage}}
            </div>
        </div>
        <div class="widget-content" v-else>
            <div v-for="entry in entries" :class="['flex margin-5', {'bottom-border': entry !== entries[entries.length - 1]}]">
                <div v-if="icon" class="flex flex-center widget-image-holder">
                    <i class="material-icons padding-5" aria-hidden="true" :style="{'background-color': icon.color, color: 'white'}" role="img">
                        {{icon.icon}}
                    </i>
                </div>
                <div class="flex flex-vertical-middle">
                    <router-link v-html="entry.text" :to="entry.link"></router-link>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
    import {WidgetDefault} from './WidgetDefault.vue';
    import {getUsername} from '@/filters/username';
    import {Component} from 'vue-facing-decorator';
    import dateHelper, {isToday} from '@/utils/portuna/dateHelper';

    interface IOpenRequestsWidgetEntryBase
    {
        date_from: string;
        date_to: Nullable<string>;
        id: number;
        requester: number;
        requester_name: string;
        text: string,
        link: string,
    }

    interface IOvertimeRequest extends IOpenRequestsWidgetEntryBase
    {
        type: 'overtime';
        sub_type: 'requested' | 'requested-change' | 'requested-revoke';
    }

    interface IVacationRequest extends IOpenRequestsWidgetEntryBase
    {
        type: 'vacation';
        sub_type: 'requested' | 'requested-revoke';
    }

    interface ICorrectionRequest extends IOpenRequestsWidgetEntryBase
    {
        type: 'correction';
        sub_type: 'requested';
    }

    interface ICreditRequest extends IOpenRequestsWidgetEntryBase
    {
        type: 'credit';
        sub_type: 'requested';
    }

    interface IAbsenceRequest extends IOpenRequestsWidgetEntryBase
    {
        type: 'absence';
        sub_type: 'requested';
    }

    type IOpenRequestsWidgetEntry =
        | IOvertimeRequest
        | IVacationRequest
        | ICorrectionRequest
        | ICreditRequest
        | IAbsenceRequest;

    @Component({
        name: 'WidgetOpenRequests',
        methods: {
            dateHelper,
            isToday,
            getUsername,
        },
    })
    export default class WidgetOpenRequests extends WidgetDefault
    {
        get entries(): IOpenRequestsWidgetEntry[]
        {
            const entries = this.content.entries as IOpenRequestsWidgetEntry[];

            return entries.map(entry =>
            {
                switch (entry.type)
                {
                    case 'absence':
                        entry.link = '/employee/absences/list#search='
                            + encodeURIComponent(
                                '{"employee":"' + entry.requester_name + '","state:strict": [0],"ovr": 1}',
                            );

                        entry.text = `${entry.requester_name} hat eine <strong>Abwesenheit</strong> von <strong>${(dateHelper('d.m.Y, H:i', entry.date_from) as string).replace(
                            ', 00:00',
                            '',
                        )}</strong> bis <strong>${(dateHelper(
                            'd.m.Y, H:i',
                            entry.date_to,
                        ) as string).replace(', 00:00', '')}</strong> beantragt.`;

                        break;
                    case 'correction':
                        entry.link = '/employee/time-tracker/correction/list#search='
                            + encodeURIComponent(
                                '{"name": "' + entry.requester_name + '","status": [0], "ovr": 1}',
                            );

                        entry.text = `${entry.requester_name} hat eine <strong>Korrektur</strong> für den <strong>${(dateHelper('d.m.Y, H:i', entry.date_from) as string).replace(
                            ', 00:00',
                            '',
                        )}</strong> beantragt.`;

                        break;
                    case 'credit':
                        entry.link = '/employee/time-tracker/overtime/credit-list#search=' + encodeURIComponent(
                            '{"name": "' + entry.requester_name + '","state": [0], "ovr": 1}',
                        );

                        entry.text = `${entry.requester_name} hat eine <strong>Gutschrift</strong> für den <strong>${dateHelper(
                            'd.m.Y',
                            entry.date_from,
                        ) as string}</strong> beantragt.`;
                        break;
                    case 'vacation':
                        if (entry.sub_type === 'requested')
                        {
                            entry.link = '/employee/vacation/list/all#search='
                                + encodeURIComponent(
                                    '{"employee_id:strict": [' + entry.requester + '],"state": [0],"employeeState": [1], "ovr": 1}',
                                );

                            entry.text = `${entry.requester_name} hat einen <strong>Urlaubsantrag</strong> für den Zeitraum <strong>${dateHelper(
                                'd.m.Y',
                                entry.date_from,
                            )}</strong> - <strong>${dateHelper(
                                'd.m.Y',
                                entry.date_to,
                            )}</strong> gestellt.`;
                        }
                        else if (entry.sub_type === 'requested-revoke')
                        {
                            entry.link = '/employee/vacation/list/all#search='
                                + encodeURIComponent(
                                    '{"employee_id:strict": [' + entry.requester + '],"revoke_state": [1],"state": [],"employeeState": [1], "ovr": 1}',
                                );

                            entry.text = `${entry.requester_name} hat einen <strong>Antrag auf Rücknahme des Urlaubsantrags</strong> für den Zeitraum vom <strong>${dateHelper(
                                'd.m.Y',
                                entry.date_from,
                            )}</strong> bis zum <strong>${dateHelper('d.m.Y', entry.date_to)}</strong> gestellt.`;
                        }
                        break;
                    case 'overtime':
                        if (entry.sub_type === 'requested')
                        {
                            entry.link = '/employee/time-tracker/overtime/request-list#search='
                                + encodeURIComponent(
                                    '{"employee_name": "' + entry.requester_name + '","state": [0], "ovr": 1}',
                                );

                            entry.text = `${entry.requester_name} hat einen <strong>Stundenantrag</strong> für die Woche vom <strong>${dateHelper(
                                'd.m.Y',
                                entry.date_from,
                            )}</strong> - <strong>${dateHelper(
                                'd.m.Y',
                                entry.date_to,
                            )}</strong> gestellt.`;
                        }
                        else if (entry.sub_type === 'requested-change')
                        {
                            entry.link = '/employee/time-tracker/overtime/request-list#search='
                                + encodeURIComponent(
                                    '{"employee_name": "' + entry.requester_name + '","state": [4], "ovr": 1}',
                                );

                            entry.text = `${entry.requester_name} hat einen <strong>Antrag auf Änderung des Stundenantrags</strong> für die Woche vom <strong>${dateHelper(
                                'd.m.Y',
                                entry.date_from,
                            )}</strong> - <strong>${dateHelper('d.m.Y', entry.date_to)}</strong> gestellt.`;
                        }
                        else if (entry.sub_type === 'requested-revoke')
                        {
                            entry.link = '/employee/time-tracker/overtime/request-list#search='
                                + encodeURIComponent(
                                    '{"employee_name": "' + entry.requester_name + '","revoke_state": [1],"state": [], "ovr": 1}',
                                );

                            entry.text = `${entry.requester_name} hat einen <strong>Antrag auf Rücknahme des Stundenantrags</strong> für die Woche vom <strong>${dateHelper(
                                'd.m.Y',
                                entry.date_from,
                            )}</strong> - <strong>${dateHelper('d.m.Y', entry.date_to)}</strong> gestellt.`;
                        }
                        break;
                }

                return entry;
            });
        }

        get icon(): Nullable<{ icon: string, color: string }>
        {
            let module = this.layoutData.modules.find(module => module.resource === 'employee');

            return module ? {
                icon: module.icon,
                color: '#' + module.color,
            } : null;
        }
    }
</script>